import './FullHeaderStyle.less';

import classNames from 'classnames';
import { List, Map } from 'immutable';
import * as React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import { ImgixImg } from '@opendoor/bricks';
import * as sessionActions from '@opendoor/shared-fe/build/store/session/actions';
import * as sessionSelectors from '@opendoor/shared-fe/build/store/session/selectors';

import { trackEvent } from '../../api/analytics';

interface IProps {
  hiddenMobile?: boolean;
  hideAccount?: boolean;
  hideNav?: boolean;
  showBrokerageLogo?: boolean;
  hideBorder?: boolean;
  leads?: List<Map<string, any>>;

  // Connected Props:
  human: Map<string, any>;
  infoIsLoaded: boolean;
  infoIsLoading: boolean;
  loggedIn: boolean;
  maybeFetchSessionInfo: () => void;
  maybeLogOut: () => void;
}

export class FullHeader extends React.Component<IProps> {
  public static defaultProps: Partial<IProps> = {
    hiddenMobile: false,
    hideAccount: false,
    hideNav: false,
  };

  public componentDidMount() {
    this.props.maybeFetchSessionInfo();
  }

  public render() {
    const { hiddenMobile, hideAccount, hideNav, showBrokerageLogo, hideBorder } = this.props;

    return (
      <div
        className={classNames('od-full-header', {
          bordered: showBrokerageLogo && !hideBorder,
          'hidden-mobile': hiddenMobile,
        })}
      >
        <div>
          {showBrokerageLogo ? (
            <ImgixImg
              className="opendoor-logo"
              src="logo-opendoor-homes.svg"
              alt="Opendoor Homes"
              height="28"
            />
          ) : (
            <a href="/">
              <img
                className="opendoor-logo"
                src="https://images.opendoor.com/source/s3/imgdrop-production/logo-opendoor.svg?service=consumer"
                alt="Opendoor"
                height="25"
              />
            </a>
          )}
        </div>
        {!hideNav && (
          <div>
            <ul className="primary-nav">
              <li className="primary-nav-item">
                <a className="menu-item" href="/">
                  Sell
                </a>
              </li>
              <li className="primary-nav-item">
                <a className="menu-item" href="/w/homes">
                  Buy
                </a>
              </li>
              <li className="primary-nav-item hidden-xs-down">
                <a className="menu-item" href="/w/how-it-works">
                  How it works
                </a>
              </li>
              <li className="primary-nav-item hidden-xs-down">
                <a className="menu-item" href="/w/pricing">
                  Pricing
                </a>
              </li>
              <li className="primary-nav-item dropdown">
                <a className="menu-item" href="#">
                  <span className="hidden-xs-down">More</span>
                </a>
                <ul className="primary-nav-submenu narrow">
                  <li className="primary-nav-submenu-item">
                    <a href="/w/about">About us</a>
                  </li>
                  <li className="primary-nav-submenu-item">
                    <a href="/w/stories">Stories</a>
                  </li>
                  <li className="primary-nav-submenu-item">
                    <a href="/w/reviews">Reviews</a>
                  </li>
                  <li className="primary-nav-submenu-item">
                    <a href="/w/faq">FAQ</a>
                  </li>
                  <li className="primary-nav-submenu-item">
                    <a href="/jobs">Jobs</a>
                  </li>
                  <li className="primary-nav-submenu-item">
                    <a href="/w/resources">Resources</a>
                  </li>
                  <li className="primary-nav-submenu-item">
                    <a href="/w/trusted-lenders">Lenders</a>
                  </li>
                  <li className="primary-nav-submenu-item">
                    <a href="/w/blog">Blog</a>
                  </li>
                  <li className="primary-nav-submenu-item">
                    <a href="/w/agents">Agents</a>
                  </li>
                  <li className="primary-nav-submenu-item">
                    <a href="/w/vendor-partnerships">Vendors</a>
                  </li>
                  <li className="primary-nav-submenu-item">
                    <a href="/w/contact-us">Contact</a>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        )}
        <div className={classNames('market-header-actions', { hidden: hideAccount })}>
          {this.props.infoIsLoaded && this.renderAccountMenu()}
        </div>
        <div className="cta-container">
          <div className="cta-accompanying-text">
            <div className="cta-accompanying-text-upper">Have a client that's selling?</div>
            <div className="cta-accompanying-text-lower">Let's help them sell in days.</div>
          </div>
          <div className="agent-resale-offer-banner-button-container">
            <a href={this.getUrl()} target="_blank">
              <button
                className="agent-resale-offer-banner-button button"
                onClick={() => this.trackClickEvent('get-an-offer')}
              >
                Get an offer
              </button>
            </a>
          </div>
        </div>
      </div>
    );
  }

  private renderAccountMenu() {
    const { human, loggedIn, leads, maybeLogOut } = this.props;

    return (
      <ul className="account-tab">
        {loggedIn ? (
          <li className="primary-nav-item">
            <div>
              <span className="hidden-xs-down">Welcome&nbsp;</span>
              <a href="/account" className="account-link">
                {human.get('first_name')}
              </a>
            </div>
            <ul className="menu primary-nav-submenu account">
              {leads &&
                leads.slice(0, 3).map((lead: Map<string, any>) => (
                  <li key={lead.get('id')} className="primary-nav-submenu-item">
                    <a href={lead.get('url')}>
                      {lead.getIn(['address', 'street'])}
                      {lead.get('readable_state') && ` (${lead.get('readable_state')})`}
                    </a>
                  </li>
                ))}
              {leads &&
                leads.size > 3 && (
                  <li className="primary-nav-submenu-item">
                    <a href="/properties">More</a>
                  </li>
                )}
              {leads && leads.size > 0 && <hr />}
              {human.get('has_resale_closing_dashboard') && (
                <li className="primary-nav-submenu-item">
                  <a href="/closing-dashboard">Closing dash</a>
                </li>
              )}
              {human.get('has_resale_closing_dashboard') && <hr />}
              {human.get('has_partnership_reporting_dashboard') && (
                <li className="primary-nav-submenu-item">
                  <a href="https://partner.opendoor.com/reports">Reporting Dashboard</a>
                </li>
              )}
              {human.get('has_partnership_referral_dashboard') && (
                <li className="primary-nav-submenu-item">
                  <a href="https://partner.opendoor.com/referral-dashboard">Referral Dashboard</a>
                </li>
              )}
              {human.get('has_partnership_reporting_dashboard') ||
              human.get('has_partnership_referral_dashboard') ? (
                <hr />
              ) : null}
              <li className="primary-nav-submenu-item">
                <a href="/account">Account</a>
              </li>
              <li className="primary-nav-submenu-item">
                <a href="#" onClick={maybeLogOut}>
                  Sign out
                </a>
              </li>
            </ul>
          </li>
        ) : (
          <li className="primary-nav-item">
            <a className="account-link" href="/login">
              Sign in
            </a>
          </li>
        )}
      </ul>
    );
  }

  private trackClickEvent(clickEvent) {
    const source = 'nav-bar';
    trackEvent('resale-offer-portal', 'click', clickEvent, { source });
  }

  private getLink(): string {
    return 'c_resaleportal_home';
  }

  private getUrl(): string {
    return `https://www.opendoor.com/t/f_partnership/${this.getLink()}/p_agent?route=agents`;
  }
}

const mapStateToProps = createSelector(
  [
    sessionSelectors.human,
    sessionSelectors.infoIsLoaded,
    sessionSelectors.infoIsLoading,
    sessionSelectors.loggedIn,
  ],
  (
    human: Map<string, any>,
    infoIsLoaded: boolean,
    infoIsLoading: boolean,
    loggedIn: boolean,
  ) => ({
    human,
    infoIsLoaded,
    infoIsLoading,
    loggedIn,
  }),
);

const mapDispatchToProps = (dispatch: any) => {
  return {
    maybeFetchSessionInfo: () => {
      dispatch(sessionActions.maybeFetchSessionInfo());
    },
    maybeLogOut: () => {
      dispatch(sessionActions.maybeLogOut());
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(FullHeader);
